import {
  Component,
  inject,
  signal,
  input,
  output,
  effect,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextarea } from 'primeng/inputtextarea';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { FieldsetModule } from 'primeng/fieldset';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { DropZoneDirective } from 'src/app/presentation/shared/file-uploader/drop-zone.directive';

import {
  BusquedaRespuestaDialogComponent,
  RespuestaLlamadaSeleccionada
} from 'src/app/presentation/common/busquedas/busqueda-respuesta-dialog.component';
import { BusquedaTipoAccesoDialogComponent } from 'src/app/presentation/common/busquedas/busqueda-tipo-acceso-dialog.component';
import {
  GestionTareaRepository,
  GuardarGestionTareaRequest
} from 'src/app/domain/service/gestion-tarea/gestion-tarea-repository';
import {
  RespuestaLlamada,
  requiereNuevaLlamada,
  requiereMotivoReporte
} from 'src/app/domain/model/respuesta-llamada/respuesta-llamada';
import { TipoCanal } from 'src/app/domain/model/tipo-canal/tipo-canal';
import { TareaBeneficiario } from 'src/app/domain/model/orden-servicio/orden-servicio';
import { sanitizeText, sanitizeTextarea } from 'src/app/common/input-utils';
import { HoraHintComponent } from 'src/app/presentation/shared/hora-hint/hora-hint.component';
import { MAX_OBSERVACIONES_GESTION_TAREA } from 'src/app/common/constants-ejb';
import { ContadorCaracteresDirective } from 'src/app/presentation/shared/contador-caracteres/contador-caracteres.directive';

/**
 * Resultado del diálogo de Ingresar Gestión
 */
export interface IngresarGestionResult {
  exito: boolean;
  mensaje?: string;
  ordenesGuardadas: number[];
}

/**
 * IngresarGestionDialogComponent
 *
 * Diálogo para ingresar gestión de tareas a múltiples órdenes.
 *
 * Legacy Reference:
 * - CallManagementInterfaceImpl.java / CallManagementInterfaceImpl.ui.xml
 * - CallManagementActivity.java - ingresarGestion()
 * - CallsManagementHandler.java - saveOrUpdateCalls()
 *
 * Campos condicionales:
 * - Fecha/Hora Nueva Gestión: visible cuando respuesta.snAplicaNuevaLlamada = 'S'
 * - Motivo del Reporte: visible cuando respuesta.nmconsecutivo = 21 (CLIENTE_REPORTADO)
 *
 * @author GIROP Migration
 */
@Component({
  selector: 'app-ingresar-gestion-dialog',
  standalone: true,
  imports: [
    ContadorCaracteresDirective,
    HoraHintComponent,
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    DropdownModule,
    DatePickerModule,
    InputTextarea,
    InputTextModule,
    ToastModule,
    TagModule,
    FieldsetModule,
    TooltipModule,
    BusquedaRespuestaDialogComponent,
    BusquedaTipoAccesoDialogComponent,
    DropZoneDirective
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>

    <p-dialog
      header="Ingresar Gestión de Tareas"
      [visible]="visible()"
      [modal]="true"
      [closable]="!guardando()"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '92vw', maxWidth: '44rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      (visibleChange)="onDialogVisibleChange($event)"
      (onShow)="onDialogShow()"
    >
        <div class="flex flex-col gap-4">
          <!-- Info de órdenes seleccionadas (acordeón) -->
          <p-fieldset
            [legend]="
              tareasSeleccionadas().length + ' orden(es) seleccionada(s)'
            "
            [toggleable]="true"
            [collapsed]="true"
            styleClass="text-sm"
          >
            <div class="flex flex-wrap gap-1">
              @for (tarea of tareasSeleccionadas(); track tarea.nmconsecutivo) {
                <p-tag
                  [value]="tarea.codigoOrden || tarea.nmconsecutivo.toString()"
                  severity="info"
                  styleClass="text-xs"
                >
                </p-tag>
              }
            </div>
          </p-fieldset>

          <!-- Fila 1: Fecha y Hora -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-700">
                Fecha <span class="text-red-500">*</span>
              </label>
              <p-datepicker
                [(ngModel)]="fechaLlamada"
                [showIcon]="true"
                dateFormat="dd/mm/yy"
                [maxDate]="fechaMaxima"
                [showButtonBar]="true"
                appendTo="body"
                styleClass="w-full"
              >
              </p-datepicker>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-700">
                Hora <span class="text-red-500">*</span>
              </label>
              <p-datepicker
                [(ngModel)]="horaLlamada"
                [showIcon]="true"
                [timeOnly]="true"
                [showSeconds]="false"
                [defaultDate]="fechaMaxima"
                hourFormat="24"
                appendTo="body"
                [showTransitionOptions]="'0ms'"
                [hideTransitionOptions]="'0ms'"
                styleClass="w-full"
              >
              </p-datepicker>
              <app-hora-hint [id]="'hora-gestion-ayuda'" />
            </div>
          </div>

          <!-- Fila 2: Persona que responde (opcional) -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700">
              Persona que Atendió
            </label>
            <input
              type="text"
              pInputText
              [(ngModel)]="personaResponde"
              (change)="personaResponde = sanitizeText(personaResponde)"
              class="w-full"
              maxlength="200"
              placeholder="Nombre de la persona que atendió"
            />
          </div>

          <!-- Fila 3: Respuesta. Legacy CallManagementInterfaceImpl: stbCallResponse + lupa
               (siLookUp → showDialogSearchResponse, buscador paginado en servidor) -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700">
              Respuesta <span class="text-red-500">*</span>
            </label>
            <div class="flex gap-1 min-w-0">
              <input
                pInputText
                readonly
                [value]="respuestaSeleccionada?.dsrespuesta || ''"
                placeholder="Buscar respuesta..."
                class="flex-1 min-w-0 cursor-pointer"
                (click)="dialogRespuestaVisible = true"
              />
              <button
                pButton
                type="button"
                icon="pi pi-search"
                class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Buscar respuesta"
                (click)="dialogRespuestaVisible = true"
              ></button>
              @if (respuestaSeleccionada) {
                <button
                  pButton
                  type="button"
                  icon="pi pi-times"
                  class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                  pTooltip="Limpiar"
                  (click)="limpiarRespuesta()"
                ></button>
              }
            </div>
          </div>

          <!-- Campos condicionales: Nueva Gestión (si snAplicaNuevaLlamada = 'S') -->
          @if (mostrarCamposNuevaLlamada()) {
            <div class="border border-yellow-200 rounded p-3">
              <div class="flex items-center gap-2 mb-3">
                <i class="pi pi-clock text-yellow-600"></i>
                <span class="font-medium text-yellow-800"
                  >Programar Nueva Gestión</span
                >
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-gray-700">
                    Fecha Nueva Gestión <span class="text-red-500">*</span>
                  </label>
                  <p-datepicker
                    [(ngModel)]="fechaNuevaLlamada"
                    [showIcon]="true"
                    dateFormat="dd/mm/yy"
                    [minDate]="fechaMinimaNuevaLlamada"
                    [showButtonBar]="true"
                    appendTo="body"
                    [showTransitionOptions]="'0ms'"
                    [hideTransitionOptions]="'0ms'"
                    styleClass="w-full"
                  >
                  </p-datepicker>
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-gray-700">
                    Hora Nueva Gestión <span class="text-red-500">*</span>
                  </label>
                  <p-datepicker
                    [(ngModel)]="horaNuevaLlamada"
                    [showIcon]="true"
                    [timeOnly]="true"
                    [showSeconds]="false"
                    hourFormat="24"
                    appendTo="body"
                    [showTransitionOptions]="'0ms'"
                    [hideTransitionOptions]="'0ms'"
                    styleClass="w-full"
                  >
                  </p-datepicker>
                  <app-hora-hint [id]="'hora-nueva-gestion-ayuda'" />
                </div>
              </div>
            </div>
          }

          <!-- Fila 4: Tipo de Canal. Legacy CallManagementInterfaceImpl: stbTipoCanales + lupa
               (siBuscarTipoCanal → UtilidadesBuscadores.buscarTipoCanal) -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700">
              Tipo de Canal <span class="text-red-500">*</span>
            </label>
            <div class="flex gap-1 min-w-0">
              <input
                pInputText
                readonly
                [value]="tipoCanalSeleccionado?.descripcion || ''"
                placeholder="Buscar tipo de canal..."
                class="flex-1 min-w-0 cursor-pointer"
                (click)="dialogTipoCanalVisible = true"
              />
              <button
                pButton
                type="button"
                icon="pi pi-search"
                class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Buscar tipo de canal"
                (click)="dialogTipoCanalVisible = true"
              ></button>
              @if (tipoCanalSeleccionado) {
                <button
                  pButton
                  type="button"
                  icon="pi pi-times"
                  class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                  pTooltip="Limpiar"
                  (click)="limpiarTipoCanal()"
                ></button>
              }
            </div>
          </div>

          <!-- Campo condicional: Motivo del Reporte (si respuesta = 21 CLIENTE_REPORTADO) -->
          @if (mostrarMotivoReporte()) {
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-700">
                Motivo del Reporte <span class="text-red-500">*</span>
              </label>
              <p-dropdown
                [options]="motivosReporte"
                [(ngModel)]="motivoReporteSeleccionado"
                optionLabel="label"
                optionValue="value"
                placeholder="Seleccione un motivo"
                styleClass="w-full"
                appendTo="body"
                (onChange)="onMotivoReporteChange()"
              >
              </p-dropdown>
            </div>
          }

          <!-- Campo condicional: Evidencia (para respuestas 19, 20, o 21 con motivo POSIBLE_AGRUPADORA) -->
          @if (mostrarEvidencia()) {
            <div class="border border-orange-200 rounded p-3">
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium text-gray-700">
                  <i class="pi pi-paperclip text-orange-600 mr-1"></i>
                  Evidencia <span class="text-red-500">*</span>
                </label>
                <input
                  #evidenciaInput
                  type="file"
                  class="hidden"
                  [accept]="EVIDENCIA_ACCEPT"
                  (change)="onEvidenciaInputChange($event)"
                />
                <div
                  appDropZone
                  (archivoSoltado)="onArchivoSeleccionado({ files: [$event] })"
                  class="flex flex-wrap items-center gap-2 p-2 w-full border border-dashed border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
                >
                  <button
                    pButton
                    type="button"
                    label="Seleccionar archivo"
                    icon="pi pi-upload"
                    class="p-button-outlined min-h-[44px]"
                    (click)="evidenciaInput.click()"
                  ></button>
                  <span class="text-sm text-gray-500 dark:text-gray-400">
                    o arrastre el archivo aquí
                  </span>
                </div>

                <p class="text-xs text-gray-500">
                  Formatos permitidos: {{ EVIDENCIA_FORMATOS_LABEL }}. Tamaño
                  máximo: {{ EVIDENCIA_MAX_SIZE_LABEL }}.
                </p>
                @if (archivoEvidencia) {
                  <div
                    class="flex items-center gap-2 text-sm text-green-700 p-2 rounded"
                  >
                    <i class="pi pi-check-circle"></i>
                    <span
                      >{{ archivoEvidencia.name }} ({{
                        formatFileSize(archivoEvidencia.size)
                      }})</span
                    >
                    <button
                      type="button"
                      class="ml-auto text-red-500 hover:text-red-700"
                      (click)="eliminarArchivo()"
                    >
                      <i class="pi pi-times"></i>
                    </button>
                  </div>
                }
              </div>
            </div>
          }

          <!-- Fila 5: Observaciones (obligatorio) -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700">
              Observaciones <span class="text-red-500">*</span>
            </label>
            <textarea
              pInputTextarea
              [(ngModel)]="observaciones"
              (change)="observaciones = sanitizeTextarea(observaciones)"
              rows="3"
              class="w-full"
              [appContadorCaracteres]="maxObservaciones"
              placeholder="Ingrese observaciones"
            >
            </textarea>
          </div>
        </div>

      <!-- Footer -->
      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cancelar"
            icon="pi pi-times"
            [outlined]="true"
            [disabled]="guardando()"
            styleClass="min-h-[44px]"
            (onClick)="cancelar()"
          >
          </p-button>
          <p-button
            label="Guardar Gestión"
            icon="pi pi-check"
            [loading]="guardando()"
            [disabled]="!formularioValido()"
            styleClass="min-h-[44px]"
            (onClick)="guardar()"
          >
          </p-button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- Sub-diálogos de búsqueda (paginados en el servidor) -->
    <app-busqueda-respuesta-dialog
      [(visible)]="dialogRespuestaVisible"
      (onSelect)="onRespuestaSeleccionada($event)"
    />

    <app-busqueda-tipo-acceso-dialog
      [(visible)]="dialogTipoCanalVisible"
      (onSelect)="onTipoCanalSeleccionado($event)"
    />
  `
})
export class IngresarGestionDialogComponent implements OnInit {
  readonly maxObservaciones = MAX_OBSERVACIONES_GESTION_TAREA;
  // === XSS Sanitization ===
  sanitizeText = sanitizeText;
  sanitizeTextarea = sanitizeTextarea;

  // === Inputs ===
  visible = input.required<boolean>();
  tareasSeleccionadas = input.required<TareaBeneficiario[]>();

  // === Outputs ===
  onClose = output<void>();
  onGestionGuardada = output<IngresarGestionResult>();

  // === Services ===
  private readonly gestionTareaRepo = inject(GestionTareaRepository);
  private readonly messageService = inject(MessageService);

  // === Estado ===
  guardando = signal(false);

  // Flags para campos condicionales
  mostrarCamposNuevaLlamada = signal(false);
  mostrarMotivoReporte = signal(false);
  mostrarEvidencia = signal(false);

  // Buscadores de Respuesta y Tipo de Canal (legacy: caja + lupa, paginados en servidor)
  dialogRespuestaVisible = false;
  dialogTipoCanalVisible = false;

  // Formulario
  fechaLlamada: Date = new Date();
  horaLlamada: Date = new Date();
  personaResponde = '';
  respuestaSeleccionada: RespuestaLlamada | null = null;
  fechaNuevaLlamada: Date | null = null;
  horaNuevaLlamada: Date | null = null;
  tipoCanalSeleccionado: TipoCanal | null = null;
  motivoReporteSeleccionado: string | null = null;
  observaciones = '';
  archivoEvidencia: File | null = null;

  // Códigos de respuesta que requieren evidencia
  private readonly RESPUESTAS_REQUIEREN_EVIDENCIA = [19, 20]; // 19: CLIENTE NO DESEA EL SERVICIO, 20: ARL PIDE NO ATENDER AL CLIENTE
  private readonly RESPUESTA_CLIENTE_REPORTADO = 21;
  private readonly MOTIVO_POSIBLE_AGRUPADORA = 'POSIBLE_AGRUPADORA';
  readonly EVIDENCIA_ACCEPT =
    '.pdf,.jpg,.jpeg,.png,.doc,.xls,.ppt,.docx,.xlsx,.pptx,.csv';
  readonly EVIDENCIA_FORMATOS_LABEL =
    'PDF, JPG, JPEG, PNG, DOC, XLS, DOCX, XLSX';
  readonly EVIDENCIA_MAX_SIZE_BYTES = 5 * 1024 * 1024;
  readonly EVIDENCIA_MAX_SIZE_LABEL = '5 MB';

  // Constantes
  fechaMaxima = new Date();
  fechaMinimaNuevaLlamada = new Date();

  // Opciones de Motivo del Reporte (cuando respuesta = 21 "CLIENTE REPORTADO PARA CAMBIO")
  motivosReporte = [
    { label: 'Posible Agrupadora', value: 'POSIBLE_AGRUPADORA' },
    { label: 'En proceso de liquidación', value: 'EN_PROCESO_LIQUIDACION' },
    { label: 'Menos de 10 trabajadores', value: 'MENOS_10_TRABAJADORES' },
    { label: 'Otros', value: 'OTROS' }
  ];

  // === Validación del Formulario ===
  /**
   * Valida si el formulario está completo.
   * Solo valida campos condicionales cuando están visibles.
   */
  formularioValido(): boolean {
    // Validaciones básicas (siempre requeridas)
    if (!this.fechaLlamada || !this.horaLlamada) return false;
    if (!this.respuestaSeleccionada) return false;
    if (!this.tipoCanalSeleccionado) return false;
    if (!this.observaciones?.trim()) return false;

    // Campos condicionales - solo validar si están visibles
    if (this.mostrarCamposNuevaLlamada()) {
      if (!this.fechaNuevaLlamada || !this.horaNuevaLlamada) return false;
    }

    if (this.mostrarMotivoReporte()) {
      if (!this.motivoReporteSeleccionado) return false;
    }

    if (this.mostrarEvidencia()) {
      if (!this.archivoEvidencia) return false;
    }

    return true;
  }

  constructor() {
    console.log('[IngresarGestion] Constructor ejecutado');

    // Effect para detectar cuando el diálogo se abre
    effect(
      () => {
        const isVisible = this.visible();
        console.log('[IngresarGestion] Effect - visible:', isVisible);
        if (isVisible) {
          this.resetFormulario();
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    console.log('[IngresarGestion] ngOnInit ejecutado');
  }

  // === Métodos ===

  /**
   * Se ejecuta cuando el diálogo se muestra (backup).
   */
  onDialogShow(): void {
    console.log('[IngresarGestion] onDialogShow() - Diálogo abierto');
    // Resetear formulario cuando se muestra el diálogo
    this.resetFormulario();
  }

  /**
   * Respuesta elegida en el buscador.
   * Legacy: CallManagementInterfaceImpl.postOpenDialog(Tpsc_respuesta_llamadasdto).
   */
  onRespuestaSeleccionada(res: RespuestaLlamadaSeleccionada): void {
    this.respuestaSeleccionada = {
      nmconsecutivo: res.nmconsecutivo,
      dsrespuesta: res.dsvalor,
      snAplicaNuevaLlamada: res.snaplicaNuevaLlamada || 'N',
      nmhorasNuevaLlamada: res.nmhorasNuevaLlamada ?? null
    };
    this.dialogRespuestaVisible = false;
    this.onRespuestaChange();
  }

  limpiarRespuesta(): void {
    this.respuestaSeleccionada = null;
    this.onRespuestaChange();
  }

  /**
   * Tipo de canal elegido en el buscador.
   * Legacy: CallManagementInterfaceImpl.seleccionaronItem("Buscar Tipo Canal").
   */
  onTipoCanalSeleccionado(tipo: TipoCanal): void {
    this.tipoCanalSeleccionado = tipo;
    this.dialogTipoCanalVisible = false;
  }

  limpiarTipoCanal(): void {
    this.tipoCanalSeleccionado = null;
  }

  private resetFormulario(): void {
    const ahora = new Date();
    console.log('[IngresarGestion] resetFormulario - fecha actual:', ahora);

    this.fechaLlamada = new Date(ahora);
    this.horaLlamada = new Date(ahora);
    this.personaResponde = '';
    this.respuestaSeleccionada = null;
    this.fechaNuevaLlamada = null;
    this.horaNuevaLlamada = null;
    this.tipoCanalSeleccionado = null;
    this.motivoReporteSeleccionado = null;
    this.observaciones = '';
    this.archivoEvidencia = null;

    // Reset flags de campos condicionales
    this.mostrarCamposNuevaLlamada.set(false);
    this.mostrarMotivoReporte.set(false);
    this.mostrarEvidencia.set(false);

    // Fecha mínima para nueva gestión es hoy
    this.fechaMinimaNuevaLlamada = new Date(ahora);
    this.fechaMaxima = new Date(ahora);

    console.log('[IngresarGestion] fechaLlamada asignada:', this.fechaLlamada);
  }

  onRespuestaChange(): void {
    // Actualizar flags de campos condicionales
    const respuesta = this.respuestaSeleccionada;
    console.log('[IngresarGestion] Respuesta seleccionada:', respuesta);
    console.log(
      '[IngresarGestion] snAplicaNuevaLlamada:',
      respuesta?.snAplicaNuevaLlamada
    );

    const aplicaNuevaLlamada = requiereNuevaLlamada(respuesta);
    console.log('[IngresarGestion] aplicaNuevaLlamada:', aplicaNuevaLlamada);

    const aplicaMotivoReporte = requiereMotivoReporte(respuesta);

    this.mostrarCamposNuevaLlamada.set(aplicaNuevaLlamada);
    this.mostrarMotivoReporte.set(aplicaMotivoReporte);

    // Actualizar flag de evidencia
    // Evidencia requerida para respuestas 19, 20
    // Para respuesta 21, depende del motivo de reporte (se evalúa en onMotivoReporteChange)
    this.actualizarMostrarEvidencia();

    // Si la respuesta tiene horas para nueva gestión, calcular fecha/hora sugerida
    // La nueva gestión = fecha/hora de la gestión actual + nmhorasNuevaLlamada
    if (aplicaNuevaLlamada && respuesta?.nmhorasNuevaLlamada) {
      // Combinar fecha y hora de la gestión actual
      const fechaHoraLlamada = new Date(this.fechaLlamada);
      fechaHoraLlamada.setHours(
        this.horaLlamada.getHours(),
        this.horaLlamada.getMinutes(),
        0,
        0
      );

      // Sumar las horas de la respuesta
      const nuevaFechaHora = new Date(fechaHoraLlamada);
      nuevaFechaHora.setHours(
        nuevaFechaHora.getHours() + respuesta.nmhorasNuevaLlamada
      );

      this.fechaNuevaLlamada = nuevaFechaHora;
      this.horaNuevaLlamada = new Date(nuevaFechaHora);

      console.log('[IngresarGestion] Nueva gestión calculada:', {
        fechaGestionActual: this.fechaLlamada,
        horaGestionActual: this.horaLlamada,
        horasASumar: respuesta.nmhorasNuevaLlamada,
        nuevaFecha: this.fechaNuevaLlamada,
        nuevaHora: this.horaNuevaLlamada
      });
    } else {
      this.fechaNuevaLlamada = null;
      this.horaNuevaLlamada = null;
    }

    // Limpiar motivo de reporte si no aplica
    if (!aplicaMotivoReporte) {
      this.motivoReporteSeleccionado = null;
    }
  }

  /**
   * Se ejecuta cuando cambia el motivo de reporte.
   * Actualiza la visibilidad del campo de evidencia.
   */
  onMotivoReporteChange(): void {
    this.actualizarMostrarEvidencia();
  }

  /**
   * Actualiza el flag mostrarEvidencia según la respuesta y motivo seleccionados.
   *
   * Reglas:
   * - Respuesta 19 (CLIENTE NO DESEA EL SERVICIO): requiere evidencia
   * - Respuesta 20 (ARL PIDE NO ATENDER AL CLIENTE): requiere evidencia
   * - Respuesta 21 (CLIENTE REPORTADO) + Motivo "Posible Agrupadora": requiere evidencia
   */
  private actualizarMostrarEvidencia(): void {
    const codigoRespuesta = this.respuestaSeleccionada?.nmconsecutivo;

    if (!codigoRespuesta) {
      this.mostrarEvidencia.set(false);
      this.archivoEvidencia = null;
      return;
    }

    // Respuestas 19 y 20 siempre requieren evidencia
    if (this.RESPUESTAS_REQUIEREN_EVIDENCIA.includes(codigoRespuesta)) {
      this.mostrarEvidencia.set(true);
      return;
    }

    // Respuesta 21 con motivo POSIBLE_AGRUPADORA requiere evidencia
    if (
      codigoRespuesta === this.RESPUESTA_CLIENTE_REPORTADO &&
      this.motivoReporteSeleccionado === this.MOTIVO_POSIBLE_AGRUPADORA
    ) {
      this.mostrarEvidencia.set(true);
      return;
    }

    // En cualquier otro caso, no requiere evidencia
    this.mostrarEvidencia.set(false);
    this.archivoEvidencia = null;
  }

  /**
   * Selección por clic: entrega el archivo y limpia el input para permitir
   * volver a elegir el mismo archivo.
   */
  onEvidenciaInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.onArchivoSeleccionado({ files: Array.from(input.files ?? []) });
    input.value = '';
  }

  /**
   * Maneja la selección de archivo de evidencia (clic o arrastre).
   * Valida tipo y tamaño; antes el tipo lo filtraba sólo p-fileUpload [accept].
   */
  onArchivoSeleccionado(event: { files: File[] }): void {
    if (event.files && event.files.length > 0) {
      const archivo = event.files[0];
      if (!this.esTipoEvidenciaPermitido(archivo)) {
        this.archivoEvidencia = null;
        this.messageService.add({
          severity: 'warn',
          summary: 'Archivo no válido',
          detail: `Tipo de archivo no permitido. Formatos permitidos: ${this.EVIDENCIA_FORMATOS_LABEL}.`
        });
        return;
      }
      if (archivo.size > this.EVIDENCIA_MAX_SIZE_BYTES) {
        this.archivoEvidencia = null;
        this.messageService.add({
          severity: 'warn',
          summary: 'Archivo no válido',
          detail: `El archivo supera el tamaño máximo permitido (${this.EVIDENCIA_MAX_SIZE_LABEL}).`
        });
        return;
      }

      this.archivoEvidencia = archivo;
      console.log(
        '[IngresarGestion] Archivo seleccionado:',
        this.archivoEvidencia.name
      );
    }
  }

  private esTipoEvidenciaPermitido(archivo: File): boolean {
    const ext = '.' + (archivo.name.split('.').pop()?.toLowerCase() ?? '');
    return this.EVIDENCIA_ACCEPT.split(',').includes(ext);
  }

  /**
   * Elimina el archivo de evidencia seleccionado.
   */
  eliminarArchivo(): void {
    this.archivoEvidencia = null;
  }

  /**
   * Formatea el tamaño del archivo en formato legible.
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Convierte un archivo a string Base64.
   *
   * @param file Archivo a convertir
   * @returns Promise con el contenido en Base64 (sin prefijo data:)
   */
  private convertirArchivoABase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remover el prefijo "data:mime/type;base64," para enviar solo el contenido
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatTime(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  async guardar(): Promise<void> {
    if (!this.formularioValido()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.guardando.set(true);

    try {
      const tareas = this.tareasSeleccionadas();
      const nmconsecutivos = tareas.map((t) => t.nmconsecutivo);

      const request: GuardarGestionTareaRequest = {
        nmconsecutivosOrden: nmconsecutivos,
        fechaLlamada: this.formatDate(this.fechaLlamada),
        horaLlamada: this.formatTime(this.horaLlamada),
        personaResponde: this.personaResponde.trim(),
        nmconsecRespuestaLlamada: this.respuestaSeleccionada!.nmconsecutivo,
        nmconsecTipoCanal: this.tipoCanalSeleccionado!.codigo,
        observaciones: this.observaciones?.trim() || undefined
      };

      // Campos condicionales
      if (
        this.mostrarCamposNuevaLlamada() &&
        this.fechaNuevaLlamada &&
        this.horaNuevaLlamada
      ) {
        request.fechaNuevaLlamada = this.formatDate(this.fechaNuevaLlamada);
        request.horaNuevaLlamada = this.formatTime(this.horaNuevaLlamada);
      }

      if (this.mostrarMotivoReporte() && this.motivoReporteSeleccionado) {
        request.motivoReporte = this.motivoReporteSeleccionado;
      }

      // Evidencia (archivo adjunto convertido a Base64)
      if (this.mostrarEvidencia() && this.archivoEvidencia) {
        const base64 = await this.convertirArchivoABase64(
          this.archivoEvidencia
        );
        request.nombreEvidencia = this.archivoEvidencia.name;
        request.evidenciaBase64 = base64;
      }

      // Llamar al repositorio para guardar
      const resultado = await this.gestionTareaRepo.guardarGestiones(request);

      if (resultado.exito) {
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: `Gestión guardada para ${nmconsecutivos.length} orden(es)`
        });

        this.onGestionGuardada.emit({
          exito: true,
          mensaje: resultado.mensaje,
          ordenesGuardadas: nmconsecutivos
        });

        this.cerrar();
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: resultado.mensaje || 'No se pudo guardar la gestión'
        });
      }
    } catch (error) {
      console.error('Error guardando gestión:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          'Ocurrió un error al guardar la gestión. Por favor intente nuevamente.'
      });
    } finally {
      this.guardando.set(false);
    }
  }

  cancelar(): void {
    this.cerrar();
  }

  cerrar(): void {
    this.resetFormulario();
    this.onClose.emit();
  }

  onDialogVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cerrar();
    }
  }
}
